import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { WLTempBorderDirective } from '../../directives/wl-temp-border.directive';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SectionClosingIconComponent } from './section-closing-icon';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { ISectionContainerBlok } from './section-container.model';
import { BackgroundShadeColorOption } from '@seven1/model';
import { NgClass, NgStyle } from '@angular/common';
import { CmsComponent } from '@seven1/angular-storyblok-cms';

@Component({
    selector: 'app-section-container',
    imports: [WLTempBorderDirective, SectionClosingIconComponent, NgClass, NgStyle, CmsComponent],
    templateUrl: './section-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class]': 'getDisplayClasses()',
    },
    animations: [
        trigger('slideInOut', [
            state(
                'in',
                style({
                    opacity: '1',
                    overflow: 'hidden',
                    height: '*',
                })
            ),
            state(
                'out',
                style({
                    opacity: '0',
                    overflow: 'hidden',
                    height: '0px',
                })
            ),
            transition('in => out', animate('400ms ease-in-out')),
            transition('out => in', animate('400ms ease-in-out')),
        ]),
    ],
})
export class SectionContainerComponent {
    private _tailwind = inject(TailwindService);

    block = input.required<ISectionContainerBlok>();

    closedIconState = signal<boolean>(false);
    helpMenuOpen = signal<'out' | 'in'>('out');
    helpMenuOpen2 = signal<'out' | 'in'>('in');

    closed = signal<boolean>(false);

    getAllClasses = computed<string[]>(() => {
        const blok = this.block();
        return [
            ...this._tailwind.getSpacingClasses(blok),
            ...this._tailwind.getBackgroundSizeClasses(blok),
            ...this._tailwind.getBackgroundPositionClasses(blok),
            ...this._tailwind.getBackgroundSizeClasses(blok),
            ...this._tailwind.getGradientDirectionClasses(blok),
            this._getGradientColorClass(blok.gradientColor || 'unset'),
            this._getBackgroundClass(blok.bgColor || 'unset'),
            blok.paddingX === 'unset' ? 'px-5' : '',
        ];
    });

    getDisplayClasses = computed<string[]>(() => {
        return this._tailwind.getDisplayClasses(this.block());
    });

    constructor() {
        effect(() => {
            const blok = this.block();
            this.closed.set(!!blok.closed);
            if (blok.closed) {
                this.toggleHelpMenu();
            }
        });
    }

    toggleHelpMenu() {
        this.helpMenuOpen.set(this.helpMenuOpen() === 'out' ? 'in' : 'out');
        this.helpMenuOpen2.set(this.helpMenuOpen2() === 'out' ? 'in' : 'out');
        this.closedIconState.set(!this.closedIconState());
    }

    toggleClosed() {
        this.closed.set(!this.closed());
    }

    private _getGradientColorClass(gradientColor: BackgroundShadeColorOption): string {
        switch (gradientColor) {
            case 'primary':
                return 'from-primary-gradient-start to-primary-gradient-end text-primary-contrast';
            case 'accent':
                return 'from-accent-gradient-start to-accent-gradient-end text-accent-contrast';
            case 'unset':
            default:
                return '';
        }
    }

    private _getBackgroundClass(bgColor: BackgroundShadeColorOption): string {
        switch (bgColor) {
            case 'primary':
                return 'bg-primary text-primary-contrast';
            case 'secondary':
                return 'bg-secondary text-secondary-contrast';
            case 'accent':
                return 'bg-accent text-accent-contrast';
            case 'grey':
                return 'bg-grey text-grey-contrast';
            default:
                return '';
        }
    }

    // bgPosition = input<AppBackgroundPositions>('top');
    //
    // gradientDirection = input<AppGradientDirections>('bottom');
}
